import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { useFormClients, type FormClientRow } from '@/api/hooks';

export interface SelectedClient {
  clientId: string;
  clientName: string;
  clientFullName: string;
  terminated?: boolean;
}

/**
 * Multi-select client picker: search by client name or ID (item 15 dropped the
 * "starts with" letter filter). Results are server-side (top 50), click a
 * row to add or remove, chips show the selection. scope 'active' = existing clients
 * (termination); 'all' = existing + terminated (reactivation).
 */
export function MultiClientPicker({
  scope, value, onChange, disabled, single, hideIds, hint,
}: {
  scope: 'active' | 'all' | 'mass'; // mass: also matches engagement name / manager
  value: SelectedClient[];
  onChange: (v: SelectedClient[]) => void;
  disabled?: boolean;
  single?: boolean; // pick exactly one client (Discontinuation)
  hideIds?: Set<string>; // left out of the results (Termination: subs of a selected master)
  hint?: string;         // short note shown under the search box
}) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const results = useFormClients(scope, search);
  const selectedIds = new Set(value.map((c) => c.clientId));
  const searched = search.trim().length >= 2;
  const rows = (results.data ?? []).filter((r) => !hideIds?.has(r.ClientID));

  function toggle(r: FormClientRow) {
    const picked = { clientId: r.ClientID, clientName: r.ClientName, clientFullName: r.ClientFullName, terminated: !!r.Terminated };
    if (selectedIds.has(r.ClientID)) {
      onChange(value.filter((c) => c.clientId !== r.ClientID));
    } else {
      onChange(single ? [picked] : [...value, picked]);
    }
  }

  return (
    <div className="space-y-2">
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((c) => (
            <span key={c.clientId} className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs">
              {c.clientFullName}
              {c.terminated ? <span className="text-accent">(terminated)</span> : null}
              {!disabled && (
                <button type="button" aria-label="Remove" className="text-muted-foreground hover:text-primary" onClick={() => onChange(value.filter((x) => x.clientId !== c.clientId))}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <label className="label">Search (Client Name or ID)</label>
          <div className="flex gap-2">
            <input
              className="input"
              placeholder="Client name or ID, then Enter…"
              value={searchInput}
              disabled={disabled}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
            />
            <button className="btn-secondary" type="button" disabled={disabled} onClick={() => setSearch(searchInput)}>Search</button>
          </div>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>

      {/* Results */}
      {searched && (
        <div className="max-h-64 overflow-auto rounded-md border border-border">
          {results.isFetching && <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>}
          {!results.isFetching && rows.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No matches. Try a different name or ID.</div>
          )}
          {rows.map((r) => {
            const on = selectedIds.has(r.ClientID);
            return (
              <button
                key={r.ClientID}
                type="button"
                disabled={disabled}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary/60 ${on ? 'bg-secondary/40' : ''}`}
                onClick={() => toggle(r)}
              >
                <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span>{r.ClientFullName}{r.Terminated ? <span className="ml-1 text-accent">(terminated)</span> : null}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
